import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Layout,
  Typography,
  Card,
  Button,
  message,
  Radio,
  Space,
  Checkbox,
  Progress,
  Menu,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { supabase } from "../supabase";

const { Title, Text, Paragraph } = Typography;
const { Sider } = Layout;

const Workbook = () => {
  const { lessonId } = useParams();
  const studentId = localStorage.getItem("studentId");
  const [lesson, setLesson] = useState(null);
  const [contents, setContents] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [sectionStartSlide, setSectionStartSlide] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState(new Set());
  const [progress, setProgress] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(true);
  useEffect(() => {
    const initializeWorkbook = async () => {
      await fetchLessonAndContents();
      const savedProgress = await loadProgress();
      if (savedProgress?.data) {
        setCurrentSlide(savedProgress.data.slideIndex || 0);
        setAnswers(savedProgress.data.currentAnswers || {});
        setCurrentSection(savedProgress.data.sectionIndex || 0);
      }
    };

    initializeWorkbook();
  }, [lessonId]);

  const loadProgress = async () => {
    try {
      const { data, error } = await supabase
        .from("student_lesson_progress")
        .select("*")
        .eq("lesson_id", lessonId)
        .eq("student_id", studentId)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      if (data) {
        setProgress(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error("Error loading progress:", error);
      message.error("Error loading progress");
      return null;
    }
  };

  const saveProgress = async (slideIndex, currentAnswers, sectionIndex) => {
    try {
      const progressData = {
        data: {
          slideIndex,
          currentAnswers,
          sectionIndex,
          lastUpdated: new Date().toISOString(),
          totalSlides: slides.length,
          percentComplete: Math.round((slideIndex / (slides.length - 1)) * 100),
        },
        status: slideIndex === slides.length - 1 ? "completed" : "in_progress",
      };

      const { data, error: fetchError } = await supabase
        .from("student_lesson_progress")
        .select("id")
        .eq("student_id", studentId)
        .eq("lesson_id", lessonId)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") throw fetchError;

      if (data?.id) {
        const { error } = await supabase
          .from("student_lesson_progress")
          .update(progressData)
          .eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("student_lesson_progress")
          .insert([
            {
              ...progressData,
              student_id: studentId,
              lesson_id: lessonId,
            },
          ]);
        if (error) throw error;
      }
    } catch (error) {
      console.error("Error saving progress:", error);
      message.error("Error saving progress");
    }
  };

  const fetchLessonAndContents = async () => {
    try {
      // Fetch lesson details
      const { data: lessonData, error: lessonError } = await supabase
        .from("lessons")
        .select("*")
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      setLesson(lessonData);

      // Fetch lesson contents
      const { data: contentsData, error: contentsError } = await supabase
        .from("lesson_contents")
        .select("*")
        .eq("lesson_id", lessonId)
        .order("order", { ascending: true });

      if (contentsError) throw contentsError;
      // Transform content and questions into slides
      const allSlides = [];

      // Add intro slide
      allSlides.push({
        type: "intro",
        content: lessonData,
      });

      // Process each content section
      contentsData.forEach((content, sectionIndex) => {
        const contentData = JSON.parse(content.content_data);
        const sectionStartIndex = allSlides.length;

        // Add content slides
        allSlides.push({
          type: "content",
          title: content.title,
          content: contentData,
          sectionIndex,
          sectionStartIndex,
        });

        if (contentData.activities) {
          contentData.activities.forEach((activity) => {
            allSlides.push({
              type: "activity",
              content: activity,
              sectionIndex,
              sectionStartIndex,
            });
          });
        }

        // Add question slides if they exist
        if (contentData.questions) {
          contentData.questions.forEach((question) => {
            allSlides.push({
              type: "question",
              content: question,
              sectionIndex,
              sectionStartIndex,
            });
          });
        }
      });

      setSlides(allSlides);
      setContents(contentsData);
    } catch (error) {
      message.error("Error loading lesson");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const finishSlides = () => {
    const lastSlide = slides[slides.length - 1];
    if (lastSlide?.type === "question") {
      if (!answers[slides.length - 1]) {
        message.warning("Please answer the question before finishing");
        return;
      }

      // Check if the answer is correct
      if (
        answers[slides.length - 1] !== lastSlide.content.answer &&
        lastSlide.content.answer !== "" &&
        answers[slides.length - 1].replace(".", "") !==
          lastSlide.content.answer.replace(".", "")
      ) {
        message.error("Incorrect answer! You need to restart this section.");
        setWrongAnswers((prev) => new Set(prev).add(slides.length - 1));

        // Reset to beginning of current section
        setCurrentSlide(lastSlide.sectionStartIndex);

        // Clear answers for this section
        const newAnswers = { ...answers };
        slides.forEach((slide, index) => {
          if (slide.sectionIndex === lastSlide.sectionIndex) {
            delete newAnswers[index];
          }
        });
        setAnswers(newAnswers);
        saveProgress(
          lastSlide.sectionStartIndex,
          newAnswers,
          lastSlide.sectionIndex
        );

        window.location.href = "/student/" + studentId + "/dashboard";

        return;
      }
    }
  };

  const nextSlide = () => {
    const currentSlideData = slides[currentSlide];

    if (currentSlideData?.type === "activity") {
      const currentAnswers = answers[currentSlide] || [];
      const totalChecklistItems = currentSlideData.content.checklist.length;

      if (currentAnswers.length < totalChecklistItems) {
        message.warning(
          "Please complete all checklist items before proceeding"
        );
        return;
      }
    }

    if (currentSlideData?.type === "question") {
      if (!answers[currentSlide]) {
        message.warning("Please answer the question before proceeding");
        return;
      }

      // Check if the answer is correct
      if (
        answers[currentSlide] !== currentSlideData.content.answer &&
        currentSlideData.content.answer !== "" &&
        answers[currentSlide].replace(".", "") !==
          currentSlideData.content.answer.replace(".", "")
      ) {
        message.error("Incorrect answer! You need to restart this section.");
        setWrongAnswers((prev) => new Set(prev).add(currentSlide));

        // Reset to beginning of current section
        setCurrentSlide(currentSlideData.sectionStartIndex);

        // Clear answers for this section
        const newAnswers = { ...answers };
        slides.forEach((slide, index) => {
          if (slide.sectionIndex === currentSlideData.sectionIndex) {
            delete newAnswers[index];
          }
        });
        setAnswers(newAnswers);
        saveProgress(
          currentSlideData.sectionStartIndex,
          newAnswers,
          currentSlideData.sectionIndex
        );
        return;
      }
    }

    const nextSlideIndex = Math.min(slides.length - 1, currentSlide + 1);
    setCurrentSlide(nextSlideIndex);
    saveProgress(nextSlideIndex, answers, currentSlideData?.sectionIndex || 0);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  if (loading || !lesson) {
    return <div>Loading...</div>;
  }

  const renderSlide = () => {
    const slide = slides[currentSlide];
    if (!slide) return null;

    switch (slide.type) {
      case "intro":
        return (
          <div style={{ textAlign: "center", paddingTop: "15vh" }}>
            <Title>{slide.content.title}</Title>
            <Text style={{ fontSize: "1.2em" }}>
              {slide.content.description}
            </Text>
          </div>
        );

      case "content":
        return (
          <div
            style={{
              padding: "8vh 15vw",
              maxWidth: "1200px",
              margin: "0 auto",
              height: "100%",
              overflowY: "auto",
            }}
          >
            <Title level={2} style={{ marginBottom: "2rem" }}>
              {slide.title}
            </Title>
            <Title level={3} style={{ marginBottom: "2rem", color: "#1890ff" }}>
              {slide.content.heroText}
            </Title>

            {slide.content.image && (
              <div style={{ marginBottom: "2rem", textAlign: "center" }}>
                <img
                  src={slide.content.image}
                  alt={slide.content.imageAlt || "Content image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "400px",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
              </div>
            )}

            {slide.content.video && (
              <div style={{ marginBottom: "2rem", textAlign: "center" }}>
                {slide.content.video.includes("youtube.com") ||
                slide.content.video.includes("youtu.be") ? (
                  <iframe
                    width="100%"
                    height="400"
                    src={slide.content.video.replace("watch?v=", "embed/")}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      maxWidth: "800px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                ) : (
                  <video
                    controls
                    style={{
                      maxWidth: "100%",
                      maxHeight: "400px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  >
                    <source src={slide.content.video} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            )}

            {slide.content.paragraphs &&
              slide.content.paragraphs.map((paragraph, index) => (
                <Paragraph
                  key={index}
                  style={{
                    fontSize: "1.2em",
                    lineHeight: "1.8",
                    marginBottom: "1.5rem",
                  }}
                >
                  {paragraph}
                </Paragraph>
              ))}
          </div>
        );

      case "activity":
        return (
          <div
            style={{
              display: "flex",
              height: "calc(100vh - 64px)",
              padding: "20px",
            }}
          >
            {/* Left side - Images */}
            <div
              style={{
                flex: "1",
                padding: "20px",
                borderRight: "1px solid #f0f0f0",
                overflowY: "auto",
              }}
            >
              {slide.content.images.map((image, index) => (
                <div key={index} style={{ marginBottom: "20px" }}>
                  <img
                    src={image.url}
                    alt={image.alt}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Button
                    onClick={() => {
                      const printWindow = window.open("", "_blank");
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Print Image</title>
                          </head>
                          <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh;">
                            <img src="${image.url}" alt="${image.alt}" style="max-width: 100%; max-height: 100vh;" />
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.focus();
                      printWindow.print();
                      printWindow.close();
                    }}
                    style={{ marginTop: "8px" }}
                  >
                    Print Image
                  </Button>
                </div>
              ))}
            </div>

            {/* Right side - Checklist */}
            <div
              style={{
                flex: "1",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card style={{ marginBottom: "20px" }}>
                <Title level={3}>{slide.content.title}</Title>
                <Text>{slide.content.description}</Text>
              </Card>

              <div style={{ flex: 1, overflowY: "auto" }}>
                {slide.content &&
                  slide.content.checklist &&
                  slide.content.checklist.length > 0 &&
                  slide.content.checklist.map((item) => (
                    <Card key={item.id} style={{ marginBottom: "10px" }}>
                      <Checkbox
                        checked={answers[currentSlide]?.includes(item.id)}
                        onChange={(e) => {
                          const currentAnswers = answers[currentSlide] || [];
                          const newAnswers = e.target.checked
                            ? [...currentAnswers, item.id]
                            : currentAnswers.filter((id) => id !== item.id);
                          setAnswers({
                            ...answers,
                            [currentSlide]: newAnswers,
                          });
                        }}
                      >
                        {item.text}
                      </Checkbox>
                    </Card>
                  ))}
              </div>
            </div>
          </div>
        );

      case "question":
        return (
          <div
            style={{
              display: "flex",
              height: "calc(100vh - 64px)",
              padding: "20px",
            }}
          >
            {/* Left side - Options */}
            <div
              style={{
                flex: "1",
                padding: "20px",
                borderRight: "0px solid #f0f0f0",
                overflowY: "auto",
              }}
            >
              <Radio.Group
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  color: "rgba(0, 0, 0, 0.65)",
                }}
                onChange={(e) =>
                  setAnswers({ ...answers, [currentSlide]: e.target.value })
                }
                value={answers[currentSlide]}
              >
                {slide.content &&
                  slide.content.options &&
                  slide.content.options.length > 0 &&
                  slide.content.options.map((option, optIndex) => (
                    <Radio.Button
                      key={optIndex}
                      value={option}
                      style={{
                        fontSize: 18,
                        height: "auto",
                        padding: "16px 20px",
                        borderRightWidth: 0,
                        borderLeftWidth: 0,
                        whiteSpace: "normal",
                        textAlign: "left",
                        marginBottom: "12px",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {option}
                    </Radio.Button>
                  ))}
              </Radio.Group>
              {!answers[currentSlide] && (
                <div style={{ marginTop: "16px", color: "#ff4d4f" }}>
                  * Please select an answer to continue
                </div>
              )}
            </div>

            {/* Right side - Question */}
            <div
              style={{
                flex: "1",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                background: slide.content.backgroundImage
                  ? `url(${slide.content.backgroundImage}) no-repeat center center / cover`
                  : "#fafafa",
                borderRadius: "8px",
                margin: "0 20px",
              }}
            >
              <Card
                style={{
                  width: "100%",
                  maxWidth: "600px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                <Text
                  strong
                  style={{
                    fontSize: "1.5em",
                    display: "block",
                    marginBottom: "24px",
                    lineHeight: "1.4",
                    color: "#262626",
                  }}
                >
                  {slide.content.question}
                </Text>
              </Card>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        theme="light"
        width={250}
        collapsible
        collapsed={menuCollapsed}
        onCollapse={(collapsed) => setMenuCollapsed(collapsed)}
        style={{
          padding: "24px 0",
          position: "fixed",
          height: "100vh",
          left: 0,
          top: 0,
          zIndex: 1,
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[currentSection.toString()]}
          style={{ height: "100%" }}
        >
          {slides.reduce((sections, slide, index) => {
            if (!sections[slide.sectionIndex]) {
              const sectionSlides = slides.filter(
                (s) => s.sectionIndex === slide.sectionIndex
              );
              const sectionProgress = Math.round(
                ((currentSlide - slide.sectionStartIndex) /
                  sectionSlides.length) *
                  100
              );

              sections[slide.sectionIndex] = (
                <Menu.Item key={slide.sectionIndex}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    {!menuCollapsed && (
                      <span>Section {slide.sectionIndex + 1}</span>
                    )}
                    <Progress
                      type="circle"
                      percent={sectionProgress}
                      width={30}
                      style={{ marginLeft: menuCollapsed ? 0 : 8 }}
                    />
                  </div>
                </Menu.Item>
              );
            }
            return sections;
          }, [])}
        </Menu>
      </Sider>
      <Layout
        style={{
          marginLeft: menuCollapsed ? 80 : 250,
          transition: "margin-left 0.2s",
          minHeight: "100vh",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          }}
        >
          <div style={{ flex: 1, overflow: "auto", position: "relative" }}>
            {renderSlide()}
          </div>

          <div
            style={{
              padding: "20px",
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              justifyContent: "space-between",
              background: "#fff",
            }}
          >
            <Button
              icon={<LeftOutlined />}
              onClick={prevSlide}
              disabled={currentSlide === 0}
            >
              Previous
            </Button>
            {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Text>Section {currentSection + 1} Progress:</Text>
              <Progress
                percent={Math.round(
                  ((currentSlide - sectionStartSlide) /
                    slides.filter((s) => s.sectionIndex === currentSection)
                      .length) *
                    100
                )}
                size="small"
                style={{ width: 120 }}
              />
            </div> */}
            {currentSlide === slides.length - 1 ? (
              <Button
                type="primary"
                icon={<RightOutlined />}
                onClick={finishSlides}
              >
                Finish
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<RightOutlined />}
                onClick={nextSlide}
                disabled={currentSlide === slides.length - 1}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </Layout>
    </Layout>
  );
};

export default Workbook;
